import React, { useContext, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import cx from 'classnames';

import RoundedButton from 'components/RoundedButton/RoundedButton';
import SpinnerRing from 'components/SpinnerRing';
import Toast from 'components/Toast/Toast';
import useToast from 'hooks/useToast';
import { useVisibility } from 'Shared/useVisibility';

import DownloadPasswordModal from './components/DownloadPasswordModal';
import RequestSnapshotModal from './components/RequestSnapshotModal';
import RetryConfirmModal from './components/RetryConfirmModal';
import RevokeReasonModal from './components/RevokeReasonModal';
import SnapshotDetailDrawer from './components/SnapshotDetailDrawer';
import SnapshotRow from './components/SnapshotRow';
import { STATUS_LABELS, STATUS_LIST } from './constants';
import styles from './DbSnapshots.module.scss';
import {
  queryKeys,
  useMutationCreateMaskJob,
  useMutationDownloadMaskJob,
  useMutationRetryMaskJob,
  useMutationRevokeMaskJob,
  useQueryMaskJob,
  useQueryMaskJobs,
} from './hooks/useDbSnapshots';
import { AuthContext } from '../Auth/AuthContext';

const getErrorMessage = (error, fallback) => error?.response?.data?.message || fallback;

const headerCells = ['Tenant / Reason', 'Requester', 'Status', 'Progress', 'Created', 'Expires', ''];

const initialFilters = { status: '', tenantId: '', requester: '', dateFrom: '', dateTo: '' };

const DbSnapshots = () => {
  const { authState } = useContext(AuthContext);
  const userRoles = authState?.user?.roles || [];
  const canRevoke = userRoles.includes('ROLE_ADMIN');
  // Retry is less destructive than revoke (it only spawns a new job, guarded by
  // the active-slot 409) and matches the page's own route access, so it is open
  // to ROLE_DEVELOPER as well as ROLE_ADMIN.
  const canRetry = userRoles.includes('ROLE_ADMIN') || userRoles.includes('ROLE_DEVELOPER');
  const queryClient = useQueryClient();
  const toast = useToast();
  const requestModal = useVisibility();
  const detailDrawer = useVisibility();
  const passwordModal = useVisibility();
  const revokeModal = useVisibility();
  const retryModal = useVisibility();

  const [filters, setFilters] = useState(initialFilters);
  const [activeJobId, setActiveJobId] = useState(null);
  const [downloadCredentials, setDownloadCredentials] = useState({ password: '', url: '' });
  const [revokeTarget, setRevokeTarget] = useState({ id: null, tenant: '' });
  const [retryTarget, setRetryTarget] = useState({ id: null, tenant: '' });

  const { isLoading: isLoadingList, data: listData } = useQueryMaskJobs(filters);
  const { isLoading: isLoadingDetail, data: detailData } = useQueryMaskJob(activeJobId);

  const {
    isPending: isCreating,
    mutate: createJob,
    error: createError,
    reset: resetCreate,
  } = useMutationCreateMaskJob();
  const { isPending: isRevoking, mutate: revokeJob } = useMutationRevokeMaskJob();
  const { isPending: isRetrying, mutate: retryJob } = useMutationRetryMaskJob();
  const { mutate: downloadJob } = useMutationDownloadMaskJob();

  const items = listData?.data?.items || [];
  const detailItem = detailData?.data || null;

  const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const handleOpenDetail = (id) => {
    setActiveJobId(id);
    detailDrawer.open();
  };

  const handleCreate = (payload) => {
    createJob(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.list(filters) });
        requestModal.close();
      },
    });
  };

  const handleDownload = (id) => {
    downloadJob(id, {
      onSuccess: (response) => {
        const { passphrase, url } = response?.data || {};
        setDownloadCredentials({ password: passphrase || '', url: url || '' });
        passwordModal.open();
      },
      onError: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.list(filters) });
      },
    });
  };

  const handleRevoke = (id) => {
    const target = items.find((item) => item.id === id);
    setRevokeTarget({ id, tenant: target?.tenantId || '' });
    revokeModal.open();
  };

  const submitRevoke = (reason) => {
    if (!revokeTarget.id) return;
    revokeJob(
      { id: revokeTarget.id, reason },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: queryKeys.list(filters) });
          revokeModal.close();
          setRevokeTarget({ id: null, tenant: '' });
        },
      }
    );
  };

  const handleRetry = (id) => {
    const target = items.find((item) => item.id === id);
    setRetryTarget({ id, tenant: target?.tenantId || '' });
    retryModal.open();
  };

  const submitRetry = () => {
    if (!retryTarget.id) return;
    retryJob(retryTarget.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.list(filters) });
        retryModal.close();
        setRetryTarget({ id: null, tenant: '' });
        toast.showSuccess('New snapshot job started');
      },
      onError: (error) => {
        retryModal.close();
        setRetryTarget({ id: null, tenant: '' });
        toast.showError(getErrorMessage(error, 'Failed to retry snapshot'));
      },
    });
  };

  return (
    <>
      <div className='mt-3 mx-3'>
        <div className={cx(styles.wrapper, 'panel p-0')}>
          <div className='px-5 py-4 border-btm d-flex align-items-center justify-content-between'>
            <div>
              <h2>DB Snapshots</h2>
              <p className='tab-subtitle'>Request and manage masked database snapshots</p>
            </div>
            <RoundedButton
              icon='icon-plus'
              text='Request Snapshot'
              color='primary'
              size='sm'
              onClick={requestModal.open}
            />
          </div>

          <div className={cx(styles.filtersBar, 'px-5')} data-testid='dbsnapshots-filters'>
            <div className={styles.filterField}>
              <label htmlFor='filter-status'>Status</label>
              <select
                id='filter-status'
                className='form-control'
                value={filters.status}
                onChange={(event) => updateFilter('status', event.target.value)}
              >
                <option value=''>All statuses</option>
                {STATUS_LIST.map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterField}>
              <label htmlFor='filter-tenant'>Tenant</label>
              <input
                id='filter-tenant'
                type='text'
                className='form-control'
                placeholder='Tenant name'
                value={filters.tenantId}
                onChange={(event) => updateFilter('tenantId', event.target.value)}
              />
            </div>
            <div className={styles.filterField}>
              <label htmlFor='filter-requester'>Requester</label>
              <input
                id='filter-requester'
                type='text'
                className='form-control'
                placeholder='Requester email'
                value={filters.requester}
                onChange={(event) => updateFilter('requester', event.target.value)}
              />
            </div>
            <div className={styles.filterField}>
              <label htmlFor='filter-date-from'>From</label>
              <input
                id='filter-date-from'
                type='date'
                className='form-control'
                value={filters.dateFrom}
                onChange={(event) => updateFilter('dateFrom', event.target.value)}
              />
            </div>
            <div className={styles.filterField}>
              <label htmlFor='filter-date-to'>To</label>
              <input
                id='filter-date-to'
                type='date'
                className='form-control'
                value={filters.dateTo}
                onChange={(event) => updateFilter('dateTo', event.target.value)}
              />
            </div>
            <RoundedButton
              text='Reset'
              color='gray'
              size='sm'
              btnStyle='outlined'
              onClick={() => setFilters(initialFilters)}
            />
          </div>

          {isLoadingList || isRevoking ? (
            <div className='d-flex justify-content-center align-items-center py-5' style={{ minHeight: 240 }}>
              <SpinnerRing width='80px' height='80px' />
            </div>
          ) : (
            <div className='mx-5 mb-4' style={{ overflowX: 'auto' }}>
              <table className='w-100' data-testid='dbsnapshots-table'>
                <thead>
                  <tr>
                    {headerCells.map((cell) => (
                      <th key={cell || 'actions'} className='text-gray-500 font-size-12 py-2'>
                        {cell}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={headerCells.length} className='text-center text-gray-500 py-5'>
                        No snapshots match the current filters.
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <SnapshotRow
                        key={item.id}
                        id={item.id}
                        tenant={item.tenantId}
                        requestedBy={item.requestedBy}
                        status={item.status}
                        reason={item.reason}
                        failureReason={item.failureReason}
                        createdAt={item.createdAt}
                        expiresAt={item.expiresAt}
                        onOpenDetail={handleOpenDetail}
                        onDownload={handleDownload}
                        onRevoke={handleRevoke}
                        onRetry={handleRetry}
                        canRevoke={canRevoke}
                        canRetry={canRetry}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <RequestSnapshotModal
        isOpen={requestModal.isOpen}
        onClose={() => {
          requestModal.close();
          resetCreate();
        }}
        onSubmit={handleCreate}
        isSubmitting={isCreating}
        submitError={createError}
      />

      <RevokeReasonModal
        isOpen={revokeModal.isOpen}
        onClose={() => {
          revokeModal.close();
          setRevokeTarget({ id: null, tenant: '' });
        }}
        onSubmit={submitRevoke}
        isSubmitting={isRevoking}
        tenant={revokeTarget.tenant}
      />

      <RetryConfirmModal
        isOpen={retryModal.isOpen}
        onClose={() => {
          retryModal.close();
          setRetryTarget({ id: null, tenant: '' });
        }}
        onSubmit={submitRetry}
        isSubmitting={isRetrying}
        tenant={retryTarget.tenant}
      />

      <SnapshotDetailDrawer
        isOpen={detailDrawer.isOpen}
        onClose={() => {
          detailDrawer.close();
          setActiveJobId(null);
        }}
        snapshot={detailItem}
        isLoading={isLoadingDetail}
      />

      <DownloadPasswordModal
        isOpen={passwordModal.isOpen}
        onClose={() => {
          passwordModal.close();
          setDownloadCredentials({ password: '', url: '' });
        }}
        password={downloadCredentials.password}
        downloadUrl={downloadCredentials.url}
      />

      <Toast {...toast.toast} onClose={toast.hideToast} />
    </>
  );
};

export default DbSnapshots;
